/**
 * MD-14 FE-LANDINGS — /transparency model card
 *
 * Public model card per Luật AI 2026 §10 transparency mandate. KV-cached
 * (TTL 1h). Cron rebuild aggregates current metrics from EVAL harness +
 * D1 corpus stats.
 *
 * Reference:
 *   - F-FE-LANDINGS.md §4.4
 *   - LEGAL-DIY.md §10
 */

export type ModelCardCorpus = {
  source: string; // 'vbpl-bientap-gateway.moj.gov.vn'
  doc_count: number;
  indexed_chunk_count: number;
  last_eff_status_sync: string; // ISO
};

export type ModelCardMetrics = {
  citation_correctness_pct: number;
  hallucination_pct: number;
  refusal_coverage_pct: number;
  retrieval_recall_at_5: number;
};

export type ModelCard = {
  generated_at: string;
  drafter: { primary: 'gemini-3.1-pro'; fallback: 'gemini-3-flash' };
  verifier: 'gemini-3-flash';
  embedding: { model: 'BGE-m3'; dimensions: 1024 };
  reranker: 'BGE-v2';
  corpus: ModelCardCorpus;
  metrics: ModelCardMetrics;
  limitations_vi: string[];
  limitations_en: string[];
  ai_label: 'Generated by Asklaw AI';
  audit_log_retention_months: 12;
  dpo_email: 'dpo@asklaw.vn';
  self_assessed_risk_class: 'limited_risk_ai';
};

const KV_KEY = 'transparency:model_card';
const KV_TTL_SEC = 3600;

const FALLBACK_LIMITATIONS_VI = [
  'Án lệ TANDTC chưa được indexed (Day-1 corpus tập trung VBQPPL).',
  'Văn bản pháp lý cấp tỉnh chỉ Hà Nội + TP.HCM trong giai đoạn Day-1.',
  'Asklaw không thay thế tư vấn của luật sư có chứng chỉ.',
];

const FALLBACK_LIMITATIONS_EN = [
  'Court precedent (án lệ) from TANDTC is not indexed in Day-1 corpus.',
  'Provincial-level legal documents are limited to Hanoi + HCMC in Day-1.',
  'Asklaw does not replace consultation with a licensed lawyer.',
];

const FALLBACK_CARD: ModelCard = {
  generated_at: '2026-05-04T00:00:00Z',
  drafter: { primary: 'gemini-3.1-pro', fallback: 'gemini-3-flash' },
  verifier: 'gemini-3-flash',
  embedding: { model: 'BGE-m3', dimensions: 1024 },
  reranker: 'BGE-v2',
  corpus: {
    source: 'vbpl-bientap-gateway.moj.gov.vn',
    doc_count: 0,
    indexed_chunk_count: 0,
    last_eff_status_sync: '2026-05-04T00:00:00Z',
  },
  metrics: {
    citation_correctness_pct: 0,
    hallucination_pct: 0,
    refusal_coverage_pct: 0,
    retrieval_recall_at_5: 0,
  },
  limitations_vi: FALLBACK_LIMITATIONS_VI,
  limitations_en: FALLBACK_LIMITATIONS_EN,
  ai_label: 'Generated by Asklaw AI',
  audit_log_retention_months: 12,
  dpo_email: 'dpo@asklaw.vn',
  self_assessed_risk_class: 'limited_risk_ai',
};

export interface TransparencyEnv {
  KV_TRANSPARENCY?: KVNamespace;
  CO_LAW_STATE?: KVNamespace;
}

export type ModelCardSource = {
  fetchCorpusStats(): Promise<ModelCardCorpus>;
  fetchMetrics(): Promise<ModelCardMetrics>;
};

export async function getModelCard(env: TransparencyEnv): Promise<ModelCard> {
  const kv = env.KV_TRANSPARENCY ?? env.CO_LAW_STATE;
  if (!kv) return FALLBACK_CARD;
  try {
    const raw = await kv.get(KV_KEY);
    if (!raw) return FALLBACK_CARD;
    return JSON.parse(raw) as ModelCard;
  } catch {
    return FALLBACK_CARD;
  }
}

export async function rebuildModelCard(
  env: TransparencyEnv,
  source: ModelCardSource
): Promise<ModelCard> {
  const kv = env.KV_TRANSPARENCY ?? env.CO_LAW_STATE;
  const corpus = await source.fetchCorpusStats().catch(() => FALLBACK_CARD.corpus);
  const metrics = await source.fetchMetrics().catch(() => FALLBACK_CARD.metrics);
  const card: ModelCard = {
    ...FALLBACK_CARD,
    generated_at: new Date().toISOString(),
    corpus,
    metrics,
  };
  if (kv) {
    try {
      await kv.put(KV_KEY, JSON.stringify(card), { expirationTtl: KV_TTL_SEC });
    } catch {
      // best-effort
    }
  }
  return card;
}

export { FALLBACK_CARD, KV_KEY as TRANSPARENCY_KV_KEY };
